import { useEffect, useRef, type ReactNode } from 'react';
import {
  Sparkles, MessageSquare, CheckCircle2, Loader2,
  AlertTriangle, Search, BarChart3, Send,
  ExternalLink, LayoutDashboard,
} from 'lucide-react';
import { useT } from '@/i18n/provider';
import { useAutoTypewriter } from './useAutoTypewriter';
import { useStepSequencer, type SequencerStep } from './useStepSequencer';

/**
 * Splits a translation containing the `{service}` placeholder and renders
 * the service name with a highlighted span in the same place.
 */
function renderWithService(text: string, serviceName: string): ReactNode {
  const parts = text.split('{service}');
  if (parts.length === 1) return text;
  const out: ReactNode[] = [];
  parts.forEach((part, i) => {
    out.push(<span key={`p-${i}`}>{part}</span>);
    if (i < parts.length - 1) {
      out.push(
        <span key={`s-${i}`} className="text-gray-900 dark:text-white font-medium">
          {serviceName}
        </span>,
      );
    }
  });
  return <>{out}</>;
}

/* ------------------------------------------------------------------ */
/*  Animation Steps                                                    */
/* ------------------------------------------------------------------ */

const STEPS: SequencerStep[] = [
  // Phase 1: type in input → send → investigation
  { id: 'input-1', duration: 3200 },
  { id: 'send-1', duration: 600 },
  { id: 'thinking-1', duration: 1200 },
  { id: 'tool-1', duration: 1000 },
  { id: 'tool-2', duration: 1000 },
  { id: 'chart-1', duration: 2800 },
  { id: 'tool-3', duration: 1000 },
  { id: 'chart-2', duration: 2800 },
  { id: 'tool-4', duration: 1000 },
  { id: 'chart-3', duration: 2800 },
  { id: 'synthesis', duration: 1200 },
  { id: 'rootcause', duration: 3500 },
  // Phase 2: follow-up question
  { id: 'input-2', duration: 2500 },
  { id: 'send-2', duration: 600 },
  { id: 'followup-thinking', duration: 1200 },
  { id: 'followup-answer', duration: 6000 },
  { id: 'hold', duration: 4000 },
];

// User questions and follow-up answer are now sourced from i18n,
// see investigatorDemo.userQ1 / userQ2 / followupAnswer.

/* ------------------------------------------------------------------ */
/*  Mock Timechart                                                     */
/* ------------------------------------------------------------------ */

const TIMECHART = [3, 2, 4, 3, 5, 4, 6, 8, 12, 15, 18, 22, 28, 45, 72, 68, 55, 48, 42, 38, 25, 18, 12, 8];

function MiniTimechart() {
  const W = 320, H = 80;
  const max = Math.max(...TIMECHART);
  const s = W / (TIMECHART.length - 1);
  const points = TIMECHART.map((v, i) => `${i * s},${H - (v / max) * (H - 8)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16" preserveAspectRatio="none">
      <defs>
        <linearGradient id="errGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(239,68,68,0.4)" />
          <stop offset="100%" stopColor="rgba(239,68,68,0.02)" />
        </linearGradient>
      </defs>
      <line x1={0} y1={H / 2} x2={W} y2={H / 2} stroke="#1D2939" strokeWidth={0.5} />
      <polygon points={`0,${H} ${points} ${W},${H}`} fill="url(#errGrad)" />
      <polyline points={points} fill="none" stroke="#ef4444" strokeWidth={1.5} />
      <circle cx={13 * s} cy={H - (TIMECHART[13] / max) * (H - 8)} r={3} fill="#ef4444" stroke="#0C111D" strokeWidth={1.5} />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Mock Deployment Bar Chart                                          */
/* ------------------------------------------------------------------ */

const DEPLOYS = [
  { time: '10:00', count: 1, highlight: false },
  { time: '11:00', count: 2, highlight: false },
  { time: '12:00', count: 0, highlight: false },
  { time: '13:00', count: 1, highlight: false },
  { time: '13:52', count: 3, highlight: true },
  { time: '14:00', count: 0, highlight: false },
  { time: '15:00', count: 1, highlight: false },
  { time: '16:00', count: 0, highlight: false },
];

function MiniDeployChart() {
  const W = 320, H = 60;
  const max = Math.max(...DEPLOYS.map(d => d.count));
  const barW = W / DEPLOYS.length;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none">
      {DEPLOYS.map((d, i) => {
        const barH = max > 0 ? (d.count / max) * (H - 16) : 0;
        return (
          <g key={i}>
            <rect
              x={i * barW + 4}
              y={H - 12 - barH}
              width={barW - 8}
              height={barH}
              rx={2}
              fill={d.highlight ? '#ef4444' : '#3B9C93'}
              opacity={d.highlight ? 1 : 0.5}
            />
            {d.highlight && (
              <>
                <line x1={i * barW + barW / 2} y1={0} x2={i * barW + barW / 2} y2={H - 12 - barH - 2} stroke="#ef4444" strokeWidth={1} strokeDasharray="3 2" />
                <text x={i * barW + barW / 2} y={8} textAnchor="middle" fill="#ef4444" fontSize={7} fontWeight="bold">v2.4.1</text>
              </>
            )}
            <text x={i * barW + barW / 2} y={H - 1} textAnchor="middle" fill="#6B7280" fontSize={7}>
              {d.time}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Mock Pie Chart                                                     */
/* ------------------------------------------------------------------ */

const PIE_DATA = [
  { label: 'payments-svc', pct: 42, color: '#ef4444' },
  { label: 'orders-svc', pct: 28, color: '#f59e0b' },
  { label: 'auth-svc', pct: 18, color: '#3b82f6' },
  { label: 'other', pct: 12, color: '#6b7280' },
];

function MiniPieChart() {
  const size = 80, cx = 40, cy = 40, r = 30, ir = 18;
  let cum = 0;
  const slices = PIE_DATA.map((d) => {
    const s0 = cum; cum += d.pct / 100;
    const a0 = s0 * 2 * Math.PI - Math.PI / 2;
    const a1 = cum * 2 * Math.PI - Math.PI / 2;
    const la = d.pct > 50 ? 1 : 0;
    return (
      <path key={d.label} fill={d.color}
        d={`M${cx + r * Math.cos(a0)},${cy + r * Math.sin(a0)} A${r},${r} 0 ${la} 1 ${cx + r * Math.cos(a1)},${cy + r * Math.sin(a1)} L${cx + ir * Math.cos(a1)},${cy + ir * Math.sin(a1)} A${ir},${ir} 0 ${la} 0 ${cx + ir * Math.cos(a0)},${cy + ir * Math.sin(a0)} Z`}
      />
    );
  });
  return (
    <div className="flex items-center gap-4">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-16 h-16 shrink-0">{slices}</svg>
      <div className="space-y-1">
        {PIE_DATA.map((d) => (
          <div key={d.label} className="flex items-center gap-2 text-[10px]">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
            <span className="text-gray-500 dark:text-gray-400">{d.label}</span>
            <span className="text-gray-700 dark:text-gray-300 font-medium ml-auto">{d.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared sub-components                                              */
/* ------------------------------------------------------------------ */

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="shrink-0 mt-0.5 rounded-full p-1.5 bg-primary-600">
        <MessageSquare className="h-3 w-3 text-white" />
      </div>
      <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed pt-0.5 font-medium">{text}</p>
    </div>
  );
}

function AgentBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="shrink-0 mt-0.5 rounded-full p-1.5 bg-amber-100 dark:bg-amber-500/20 ring-1 ring-amber-300 dark:ring-amber-500/30">
        <Sparkles className="h-3 w-3 text-amber-600 dark:text-amber-400" />
      </div>
      <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed pt-0.5 min-w-0">{children}</div>
    </div>
  );
}

function ToolStep({ label, query, done }: { label: string; query: string; done: boolean }) {
  return (
    <div className="ml-9 rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400">
        {done
          ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400 shrink-0" />
          : <Loader2 className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 animate-spin shrink-0" />}
        <span>{label}</span>
      </div>
      {done && (
        <div className="px-3 pb-2 border-t border-gray-200/60 dark:border-gray-800/60">
          <code className="text-[10px] font-mono text-amber-700 dark:text-amber-300/80">{query}</code>
        </div>
      )}
    </div>
  );
}

function VizCard({ title, children }: { title: string; children: React.ReactNode }) {
  const t = useT();
  return (
    <div className="ml-9 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60 overflow-hidden">
      <div className="px-3 pt-2.5 pb-1">
        <div className="flex items-center gap-1.5">
          <BarChart3 className="h-3 w-3 text-primary-500 dark:text-primary-400" />
          <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{title}</span>
        </div>
      </div>
      <div className="px-3 pb-2">{children}</div>
      {/* Action buttons */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-t border-gray-200/60 dark:border-gray-800/60">
        <button className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
          <ExternalLink className="h-2.5 w-2.5" />
          <span>{t('investigatorDemo.openInSearch')}</span>
        </button>
        <button className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
          <LayoutDashboard className="h-2.5 w-2.5" />
          <span>{t('investigatorDemo.addToDashboard')}</span>
        </button>
      </div>
    </div>
  );
}

function RootCauseCard() {
  const t = useT();
  const timeline = [
    { time: '13:52', event: t('investigatorDemo.timelineEvent1') },
    { time: '13:55', event: t('investigatorDemo.timelineEvent2') },
    { time: '14:02', event: t('investigatorDemo.timelineEvent3') },
  ];
  return (
    <div className="ml-9 rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-emerald-200 dark:border-emerald-700/50">
        <AlertTriangle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
        <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
          {t('investigatorDemo.rootCauseTitle')}
        </span>
        <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30">
          {t('investigatorDemo.rootCauseConfidence')}
        </span>
      </div>
      <div className="px-3 py-2.5 space-y-2.5">
        <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
          {renderWithService(t('investigatorDemo.rootCauseSummary'), 'payments-svc')}
        </p>
        <div>
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {t('investigatorDemo.rootCauseTimelineLabel')}
          </span>
          <div className="mt-1 space-y-1">
            {timeline.map((e) => (
              <div key={e.time} className="flex items-start gap-2 text-[10px]">
                <span className="font-mono text-gray-400 dark:text-gray-500 shrink-0">{e.time}</span>
                <span className="text-gray-600 dark:text-gray-400">{e.event}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {t('investigatorDemo.recommendationLabel')}
          </span>
          <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5">
            {t('investigatorDemo.recommendationBody')}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

interface InvestigatorDemoProps {
  enabled?: boolean;
}

export function InvestigatorDemo({ enabled = true }: InvestigatorDemoProps) {
  const t = useT();
  const userQ1 = t('investigatorDemo.userQ1');
  const userQ2 = t('investigatorDemo.userQ2');

  const { currentStepId, stepIndex } = useStepSequencer({
    steps: STEPS,
    enabled,
    loop: true,
  });

  const step = currentStepId;

  // Typewriter for question 1 (in input bar)
  const { displayedText: typed1 } = useAutoTypewriter({
    text: userQ1,
    speed: 30,
    startDelay: 300,
    enabled: enabled && stepIndex === 0,
  });

  // Typewriter for question 2 (in input bar)
  const { displayedText: typed2 } = useAutoTypewriter({
    text: userQ2,
    speed: 35,
    startDelay: 300,
    enabled: enabled && stepIndex === 12,
  });

  const show = (minStep: string) => {
    const idx = STEPS.findIndex((s) => s.id === minStep);
    return stepIndex >= idx;
  };

  // Determine what's in the input bar
  const isInputPhase1 = step === 'input-1';
  const isInputPhase2 = step === 'input-2';
  const inputText = isInputPhase1 ? typed1 : isInputPhase2 ? typed2 : '';
  const showInputCursor = isInputPhase1 || isInputPhase2;

  // Question 1 is in chat after send-1
  const q1InChat = show('send-1');
  // Question 2 is in chat after send-2
  const q2InChat = show('send-2');

  // Status
  const isAnalyzing = show('thinking-1') && !show('rootcause');
  const isFollowupThinking = step === 'followup-thinking';
  const isComplete = show('rootcause') && !show('input-2');
  const isFollowupComplete = show('followup-answer');

  // Auto-scroll
  const feedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTo({ top: feedRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [stepIndex]);

  return (
    <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-950 shadow-2xl overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-200/60 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shrink-0">
        <div className="w-7 h-7 rounded-lg bg-amber-500/10 ring-1 ring-amber-500/20 flex items-center justify-center">
          <Sparkles className="h-3.5 w-3.5 text-amber-400" />
        </div>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('investigatorDemo.statusName')}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {(isAnalyzing || isFollowupThinking) && (
            <>
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-xs text-amber-400">{t('investigatorDemo.statusAnalyzing')}</span>
            </>
          )}
          {(isComplete || isFollowupComplete) && !isInputPhase2 && !isFollowupThinking && (
            <>
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-xs text-emerald-400">{t('investigatorDemo.statusComplete')}</span>
            </>
          )}
          {!q1InChat && (
            <>
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-xs text-gray-400">{t('investigatorDemo.statusOnline')}</span>
            </>
          )}
        </div>
      </div>

      {/* Chat feed */}
      <div ref={feedRef} className="h-[400px] sm:h-[500px] overflow-y-auto [&::-webkit-scrollbar]:hidden px-3 sm:px-4 py-3 space-y-2 bg-white dark:bg-gray-950">
        {/* Question 1 in chat (after send) */}
        {q1InChat && <UserBubble text={userQ1} />}

        {/* AI thinking */}
        {show('thinking-1') && !show('tool-1') && (
          <AgentBubble>
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="h-3 w-3 animate-spin text-amber-400" />
              <span className="text-xs">{t('investigatorDemo.thinkingErrors')}</span>
            </div>
          </AgentBubble>
        )}

        {/* Tool steps + charts */}
        {show('tool-1') && (
          <ToolStep label={t('investigatorDemo.toolSearchErrors')}
            query="level=error source=api-gateway earliest=-6h | timechart span=15m count"
            done={show('tool-2')} />
        )}
        {show('tool-2') && (
          <ToolStep label={t('investigatorDemo.toolDistribution')}
            query="level=error source=api-gateway earliest=-6h | stats count by service"
            done={show('chart-1')} />
        )}
        {show('chart-1') && (
          <VizCard title={t('investigatorDemo.chartTimechart')}>
            <MiniTimechart />
            <div className="flex items-center justify-between mt-1 text-[9px] text-gray-500 font-mono">
              <span>08:00</span><span>12:00</span><span>14:00</span><span>18:00</span><span>now</span>
            </div>
          </VizCard>
        )}
        {show('tool-3') && (
          <ToolStep label={t('investigatorDemo.toolByUpstream')}
            query="level=error source=api-gateway earliest=-2h | stats count by upstream_service"
            done={show('chart-2')} />
        )}
        {show('chart-2') && (
          <VizCard title={t('investigatorDemo.chartPie')}>
            <MiniPieChart />
          </VizCard>
        )}

        {/* Tool step 4: correlate with deploys */}
        {show('tool-4') && (
          <ToolStep label={t('investigatorDemo.toolDeploys')}
            query="index=deploy_logs earliest=-6h | timechart span=30m count by service"
            done={show('chart-3')} />
        )}
        {show('chart-3') && (
          <VizCard title={t('investigatorDemo.chartDeployments')}>
            <MiniDeployChart />
          </VizCard>
        )}

        {/* Synthesis */}
        {show('synthesis') && (
          <AgentBubble>
            {show('rootcause') ? (
              <span>{renderWithService(t('investigatorDemo.synthesisFound'), 'payments-svc')}</span>
            ) : (
              <div className="flex items-center gap-2 text-gray-400">
                <Loader2 className="h-3 w-3 animate-spin text-amber-400" />
                <span className="text-xs">{t('investigatorDemo.thinkingDeploys')}</span>
              </div>
            )}
          </AgentBubble>
        )}
        {show('rootcause') && <RootCauseCard />}

        {/* Follow-up question in chat (after send-2) */}
        {q2InChat && <UserBubble text={userQ2} />}

        {/* Follow-up thinking */}
        {show('followup-thinking') && !show('followup-answer') && (
          <AgentBubble>
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="h-3 w-3 animate-spin text-amber-400" />
              <span className="text-xs">{t('investigatorDemo.thinkingOwnership')}</span>
            </div>
          </AgentBubble>
        )}

        {/* Follow-up answer */}
        {show('followup-answer') && (
          <AgentBubble>
            <span className="text-sm text-gray-300 leading-relaxed">{t('investigatorDemo.followupAnswer')}</span>
          </AgentBubble>
        )}
      </div>

      {/* Input bar */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-950">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
          <Search className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
          <div className="flex-1 min-w-0">
            {inputText ? (
              <span className="text-sm text-gray-800 dark:text-gray-200">
                {inputText}
                {showInputCursor && (
                  <span className="inline-block w-[2px] h-3.5 bg-gray-800 dark:bg-gray-300 align-text-bottom animate-pulse ml-[1px]" />
                )}
              </span>
            ) : (
              <span className="text-sm text-gray-500">{t('investigatorDemo.inputPlaceholder')}</span>
            )}
          </div>
          <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 transition-colors ${
            inputText ? 'bg-amber-600' : 'bg-gray-200 dark:bg-gray-800'
          }`}>
            <Send className={`h-3 w-3 ${inputText ? 'text-white' : 'text-gray-400 dark:text-gray-600'}`} />
          </div>
        </div>
      </div>
    </div>
  );
}
